import { Injectable } from '@angular/core';
import {AcceptInviteRequest, InviteUserRequest, OrganisationDto, UpdateUserInviteRequest, UserDto, UserService} from "../../api";
import {ApiHelperService} from "../api-helper-service/api-helper.service";
import {catchError, ReplaySubject, throwError} from "rxjs";

@Injectable({
  providedIn: 'root'
})
export class UserInfoService {

  private _userData : UserDto = {};
  public orgSubject$ = new ReplaySubject<OrganisationDto>()

  public userData$ = new ReplaySubject<UserDto>()

  private planPriorities: { [key: string]: number } = {
    'FREE': 0,
    'BASIC': 1,
    'PRO': 2,
    'ENTERPRISE': 3
  };

  constructor(public apiService : UserService,
              public apiHelper : ApiHelperService) { }

  public getUserInfo(){
    let obs = this.apiService.getUserDataEndpoint().pipe(
      catchError((e, obs) => {
        this.apiHelper.onError(e)
        return throwError(() => e);
      }));

    obs.subscribe({
      next: res => {
        this._userData = res;
        this.userData$.next(res);

      },
      error: () => {
        this.userData$.next({})

        this._userData = {};
      },
      complete: () => {

      }
    })
    return obs;
  }

  public changeOrgName(name : string){
    let obs = this.apiService.changeOrgaNameEndpoint({name: name}).pipe(
      catchError((e, obs) => {
        this.apiHelper.onError(e)
        return throwError(() => e);
      }));
    return obs;
  }
  public getFullUser(id : string){
    let obs = this.apiService.getFullUserEndpoint(id).pipe(
      catchError((e, obs) => {
        this.apiHelper.onError(e)
        return throwError(() => e);
      }));

    obs.subscribe()
    return obs;
  }

  public getInvitedUser(){
    let obs = this.apiService.getAllUserInviteEndpoint().pipe(
      catchError((e, obs) => {
        this.apiHelper.onError(e)
        return throwError(() => e);
      }));

    return obs;
  }

  public deleteUser(email : string){
    let obs = this.apiService.deleteUserEndpoint({
      eMail: email
    }).pipe(
      catchError((e, obs) => {
        this.apiHelper.onError(e)
        return throwError(() => e);
      }));

    return obs;
  }

   public deactivateUser(userId: string) {
    let obs = this.apiService.deactivateUserEndpoint({
      targetUserId: userId
    }).pipe(
      catchError((e, obs) => {
        this.apiHelper.onError(e)
        return throwError(() => e);
      }))
    return obs;
  }

  public reactivateUser(userId: string) {
    let obs = this.apiService.reactivateUserEndpoint({
      targetUserId: userId
    }).pipe(
      catchError((e, obs) => {
        this.apiHelper.onError(e)
        return throwError(() => e);
      }))
    return obs;
  }

  public deleteUserInvite(inviteCode: string) {
    let obs = this.apiService.deleteUserInviteEndpoint(inviteCode).pipe(
      catchError((e, obs) => {
        this.apiHelper.onError(e)
        return throwError(() => e);
      }));
    return obs;
  }

  public updateUserInvite(inviteCode: string, firstname: string, lastname: string, email: string, roles: string[]) {
    const request: UpdateUserInviteRequest = { firstname, lastname, email, roles };
    return this.apiService.updateUserInviteEndpoint(inviteCode, request).pipe(
      catchError((e, obs) => {
        this.apiHelper.onError(e);
        return throwError(() => e);
      })
    );
  }

  public resendUserInvite(inviteCode: string) {
    return this.apiService.resendUserInviteEndpoint(inviteCode).pipe(
      catchError((e, obs) => {
        this.apiHelper.onError(e);
        return throwError(() => e);
      })
    );
  }

  public getInviteInfo(code : string){
    let obs = this.apiService.getUserInviteEndpoint(code).pipe(
      catchError((e, obs) => {
        this.apiHelper.onError(e)
        return throwError(() => e);
      }));

    return obs;
  }

  public Invite(firstname : string, lastname : string, mail : string, roles : string[] ){
    let invite = {
      invitedUserFirstname: firstname,
      invitedUserLastname: lastname,
      inviteMail: mail,
      roles: roles
    } as InviteUserRequest

    let obs = this.apiService.inviteUserEndpoint(invite).pipe(
      catchError((e, obs) => {
        this.apiHelper.onError(e)
        return throwError(() => e);
      }));
    return obs;
  }

  public AcceptInvite(code : string, password: string){
    let invite = {
      password: password,
      code: code
    } as AcceptInviteRequest

    let obs = this.apiService.acceptUserInviteEndpoint(invite).pipe(
      catchError((e, obs) => {
        this.apiHelper.onError(e)
        return throwError(() => e);
      }));
    return obs;
  }

  setRole(role : string, userid : string, remove: boolean){
    let obs = this.apiService.postRoleUserEndpoint(
      {
        requestedUserId: userid,
        roleKey: role,
        remove: remove
      }
    ).pipe(
      catchError((e, obs) => {
        this.apiHelper.onError(e)
        return throwError(() => e);
      }))
    return obs;
  }

  getOrg(){
    this.apiService.getOrgaEndpoint().pipe(
      catchError((e, obs) => {
        this.apiHelper.onError(e)
        return throwError(() => e);
      })
    ).subscribe({
        next: res => {
          this.orgSubject$.next(res);
        },
        error: () => {
          this.orgSubject$.next({})
        },
        complete: () => {
        }
      }
    )
  }

  updateUserName(firstname: string, lastname: string, userid: string) {
    let obs = this.apiService.updateUserNameEndpoint(
      {
        requestedUserId: userid,
        firstname: firstname,
        lastname: lastname
      }
    ).pipe(
      catchError((e, obs) => {
        this.apiHelper.onError(e)
        return throwError(() => e);
      }))
    return obs;
  }

  hasRole(role: string){
    return this._userData?.roles?.find(r => r.name == role) != null;
  }

  isAdminUser(){
    return this.hasRole(Roles.OWNER) || this.hasRole(Roles.ADMIN);
  }

  /**
   * Checks if the user is a system admin (belongs to Docuty/Insirion organisation)
   * This allows access to admin-only features like system template management
   */
  isSystemAdmin(): boolean {
    const orgName = this._userData?.organisation?.name?.toLowerCase().trim() || '';
    const systemOrgs = ['docuty', 'insirion', 'docuty gmbh', 'insirion gmbh'];
    return this.isAdminUser() && systemOrgs.some(org => orgName === org);
  }

  public getCurrentPlan(): string {
    return this._userData?.organisation?.subscription?.key || 'FREE';
  }

  public hasPlanAccess(requiredPlan: string): boolean {
    const currentPlan = this.getCurrentPlan();
    const currentPriority = this.planPriorities[currentPlan] || 0;
    const requiredPriority = this.planPriorities[requiredPlan] || 999;

    return currentPriority >= requiredPriority;
  }

  public isPlanOrHigher(plan: string): boolean {
    return this.hasPlanAccess(plan);
  }

  public canInviteUsers(): boolean {
    return this.isAdminUser() && this.hasPlanAccess('BASIC');
  }

  public canGenerateApiKeys(): boolean {
    return this.isAdminUser() && this.hasPlanAccess('PRO');
  }

  public canDownloadDocuments(): boolean {
    return this.hasPlanAccess('BASIC');
  }

  public canCreateCustomTemplates(): boolean {
    return this.hasPlanAccess('PRO');
  }

  public canAccessWebVersion(): boolean {
    return this.hasPlanAccess('BASIC');
  }
}

export const Roles = {
  OWNER: "OWNER",
  ADMIN: "ADMIN",
  BACKOFFICE_USER: "BACKOFFICE_USER",
  APP_USER: "APP_USER"
}

export enum SubscriptionTier {
  FREE = 'FREE',
  BASIC = 'BASIC',
  PRO = 'PRO',
  ENTERPRISE = 'ENTERPRISE'
}
